export class AddressService {
    constructor(AddressUtilitiesService, GooglePlaceService, locationService,
        MapboxService, cartService, calloutService, blockingCalloutService, $q) {
        'ngInject';
        this.AddressUtilitiesService = AddressUtilitiesService;
        this.GooglePlaceService = GooglePlaceService;
        this.locationService = locationService;
        this.MapboxService = MapboxService;
        this.cartService = cartService;
        this.calloutService = calloutService;
        this.blockingCalloutService = blockingCalloutService;
        this.$q = $q;
        this.clearAddress();
        this.clearAddressErrors();
    }

    setAddress(newAddress) {
        this.clearAddress();
        this.data = angular.copy(newAddress);
        this.clearAddressErrors();
    }

    _setAddressErrors(error) {
        angular.merge(this.errors, error);
        return error.errorMessage;
    }

    clearAddress() {
        this.data = {
            address1: null,
            address2: null,
            city: null,
            guid: null,
            latitude: null,
            longitude: null,
            name: null,
            notes: null,
            shouldSave: false,
            state: null,
            zipCode: null,
        };
    }

    clearAddressErrors() {
        this.errors = {
            errorMessage: null,
            suggestedAddresses: []
        };
    }

    validate(address = this.data) {
        let needsValidation = !this.isComplete(address);
        if(needsValidation) return this.geocodeAddress(address);
        return this.$q.resolve(address);
    }

    validateWithDeliveryRange(address = this.data) {
        /*
         * Need to chain the promises this way in order to resolve with validatedAddress for
         * consistency with how geocodeAddress() resolves.
         */
        return this.validate(address).then(
            validatedAddress => this.locationService.verifyDeliveryRange(
                validatedAddress.latitude,
                validatedAddress.longitude
            ).then(
                () => this.$q.resolve(validatedAddress),
                error => {
                    const { message } = error;

                    this.blockingCalloutService.show(message);
                    return this.$q.reject(message);
                }
            )
        );
    }

    updateDeliveryFee(address = this.data) {
        const previousDeliveryFee = this.cartService.data.cart.check.deliveryFee;

        return this.cartService.updateCart({
            tipAmount: null,
            paymentMethod: null,
            skipCallout: true,
            deliveryInfo: address
        }).then(cart => {
            const newDeliveryFee = cart.check.deliveryFee;
            if (previousDeliveryFee !== newDeliveryFee) {
                this.calloutService.info('Delivery cost updated.');
            }

            return this.$q.resolve(address);
        });
    }

    _geocodeMapbox(address, permanent = false) {
        return this.MapboxService.geocode(address, permanent).then(
            resp => {
                let found = this.AddressUtilitiesService.findAddressInMapboxList(address, resp);
                let valid = this.isComplete(found);
                if(found && valid) return this.$q.resolve(found);
                let suggestions = resp.map(addr => this.AddressUtilitiesService.transformMapboxAddress(addr));
                let err = this._generateInvalidMessage(suggestions);
                return this.$q.reject(err);
            },
            () => this.$q.reject(this._generateInvalidMessage())
        );
    }

    _geocodeGoogle(address) {
        return this.GooglePlaceService.geocode(address).then(
            resp => {
                let found = this.AddressUtilitiesService.findAddressInGoogleList(address, resp);
                let valid = this.isComplete(found);
                if(found && valid) return this.$q.resolve(found);
                let suggestions = resp.map(addr => this.AddressUtilitiesService.transformGoogleAddress(addr));
                let err = this._generateInvalidMessage(suggestions);
                return this.$q.reject(err);
            },
            () => this.$q.reject(this._generateInvalidMessage())
        );
    }

    geocodeAddress(address, permanent = false) {
        return this._geocodeMapbox(address, permanent);
    }

    _generateInvalidMessage(suggestedAddresses = [], errorMessage = null) {
        if (!errorMessage) {
            errorMessage = 'Address was not found, please select an address.';

            if (!suggestedAddresses.length) {
                errorMessage = 'Address was not found, please enter a valid address.';
            }
        }

        let err = { errorMessage, suggestedAddresses };
        this._setAddressErrors(err);
        return err;
    }

    isComplete(address = this.data) {
        const latitudeExists = angular.isNumber(address.latitude);
        const longitudeExists = angular.isNumber(address.longitude);
        const addressComponents = ['address1', 'city', 'state', 'zipCode'];
        const addressComponentsExist = addressComponents
            .every(key => angular.isString(address[key]) && address[key].length);

        return latitudeExists && longitudeExists && addressComponentsExist;
    }

}
